<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form class="mb-3" validate-on="input lazy" @submit.prevent="updateType">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Update Type</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat"
                           @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col v-if="user.type==='admin'" class="py-0" cols="12" sm="6">
                            <company-select v-model="formData['company_id']" :items="companies" density="compact" label="Company">
                            </company-select>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['name']" autofocus :rules="[vRules.required]" density="compact"
                                          label="Name"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-img :src="image" width="200"/>
                            <v-file-input v-model="formData['file']" accept="image/*" density="compact"
                                          label="Select Image" @change="imagePreview"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['font_text']" :rules="[vRules.required]" density="compact"
                                          label="Font Text"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['font_size']" :rules="[vRules.required,vRules.gte(8)]"
                                          density="compact" label="Font Size (px)" min="8" type="number"/>
                        </v-col>
                        <v-col class="py-0 text-capitalize" cols="12" sm="6">
                            <v-select v-model="formData['category']"
                                      :items="assetCategories"
                                      :rules="[vRules.required]"
                                      density="compact"
                                      label="Category">
                            </v-select>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['status']" :items="['enabled','disabled']"
                                      :rules="[vRules.required]" density="compact" label="Status">
                            </v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-color-picker v-model="formData['font_color']" :rules="[vRules.required]"
                                            density="compact" hide-inputs label="Font Color"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import CompanySelect from "@/components/globals/company-select.vue";

const snackbar = useSnackbarStore();
const {user} = useAuthStore();
const props = defineProps({
    modelValue: Boolean,
    assetType: {
        type: Object
    }
});
const assetCategories = [
    {
        value: 'vehicle',
        title: 'Vehicle'
    },
    {
        value: 'equipment',
        title: 'Equipment'
    },
    {
        value: 'personal',
        title: 'Personal'
    },
    {
        value: 'livestock',
        title: 'Livestock'
    },
    {
        value: 'location-anchor',
        title: 'Location Anchor'
    },
    {
        value: 'building',
        title: 'Building'
    }
];
const formData = reactive({
    valid: false,
    file: null,
    _method: 'put',
    name: props.assetType.name ?? null,
    category: props.assetType.category ?? null,
    company_id: props.assetType.company_id ?? null,
    font_text: props.assetType.font_text ?? null,
    font_size: props.assetType.font_size ?? null,
    font_color: props.assetType.font_color ?? null,
    status: props.assetType.status ?? null,
});
const image = ref(props.assetType.photo);
const emits = defineEmits(['update:modelValue', 'updateAssetType']);
const dialogSwitch = ref(props.modelValue);
const companies = ref([]);
const errors = ref([]);

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompanies();
    }
})

const imagePreview = () => {
    formData['file'] = formData['file'];
    image.value = URL.createObjectURL(formData['file']);
}

const fetchCompanies = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Companies could not be loaded', 'error')
        })
}
const updateType = () => {
    errors.value = [];
    send('post', `asset-types/${props.assetType.id}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('updateAssetType', {
                    ...response.data.data.asset_type,
                    company_name: response.data.data.asset_type.company?.company_name ?? ''
                });
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
